import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  emptyFeedbackContent,
  emptyLessonsContent,
  emptyTutorsContent,
  schoolTabs,
} from '@app/schools/school-page/school-page.variables';
import {
  AccountService,
  AuthService,
  CommentService,
  ContentService,
  MetaService,
  SchoolsService,
  ScreenService,
  UserServicesService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { captureException } from '@sentry/angular';
import { getMe } from '@store/reducers/profile.reducer';
import {
  Content,
  CSchool,
  EContentPaneFormat,
  ESchoolParticipantRole,
  FindAccountStrategyEnum,
  FindContentStrategyEnum,
  FindUserServiceStrategyEnum,
  IContentFilterBlock,
  IContentPane,
  IPagination,
  LessonContentType,
  ReviewListItem,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, combineLatest, finalize, fromEvent, mergeAll, of, Subscription, takeUntil, tap } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import striptags from 'striptags';

@Component({
  selector: 'app-school-page',
  templateUrl: './school-page.component.html',
  styleUrls: ['./school-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  // DI
  private activateRoute = inject(ActivatedRoute);
  private screenService = inject(ScreenService);
  private schoolService = inject(SchoolsService);
  private location = inject(Location);
  private metaService = inject(MetaService);
  private contentService = inject(ContentService);
  private accountService = inject(AccountService);
  private userServicesService = inject(UserServicesService);
  private commentService = inject(CommentService);
  private authService = inject(AuthService);
  public deviceService = inject(DeviceDetectorService);
  private router = inject(Router);
  private store = inject(Store);

  @ViewChild('navPosition') navPosition: ElementRef;
  @Input() isModal: boolean;
  @Input() slug: string;

  @Input()
  public set subjectId(id: number) {
    this._subjectId = id;
    if (this.school) {
      this.generatePanes();
    }
  }

  public get subjectId() {
    return this._subjectId;
  }

  public tutors: IContentPane;
  public feedback: IContentPane;
  public lessons: IContentPane;
  public panesTab: IContentFilterBlock[];
  public pathName = 'main';
  public description: string;
  public loaded: boolean;
  public lessonsItems: Content[];
  public topFromHeader: number;

  public emptyTutorsContent = emptyTutorsContent;
  public emptyLessonsContent = emptyLessonsContent;
  public emptyFeedbackContent = emptyFeedbackContent;

  public init$$: Subscription;
  public hasTutors = false;
  public hasClasses = false;
  public hasLessons = false;
  public hasFeedback = false;

  private defaultPagination: IPagination = {
    page: 1,
    pageSize: 1,
  };

  public reviews: ReviewListItem[];
  public isContentSelectorOpened = false;
  public isContentSelectorVisible = true;
  public ownersIds: string[];
  public teachersIds: string[];
  public freezeTimer = false;
  public stuckState: boolean;

  private _slug: string;
  private _school: CSchool;
  private _subjectId: number;
  private _me: User;

  ngOnInit(): void {
    this._slug = this.slug ? this.slug : SchoolsService.getSchoolPostfix(this.activateRoute.snapshot.params['slug']);
    this.pathName = this.isModal ? 'main' : this.router.url.split('/')[3];
    this.panesTab = schoolTabs;

    combineLatest([
      this.store.select(getMe),
      this.schoolService.getSchoolByIdOrSlug(this._slug, ['participants', 'participants:stats', 'stats'], 'LOAD_TIME'),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([me, school]) => {
          this._school = school;
          this._me = me;
          if (!this.subjectId) {
            this.subjectId = school.subjects[0]?.id;
          }
          this.description = this._school?.description;
          this.schoolService.setCurrentSchool(school);
          this.loaded = true;
          this.init();
          this.generatePanes();
          this.setupMeta();
          this.afterLoaded();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loaded = true;
        },
      });

    if (this.deviceService.isMobile() && this.isBrowser) {
      this.setTimeout(() => {
        this.topFromHeader = this.isModal ? 0 : document.getElementsByClassName('header')[0].clientHeight;
        const stickyElm = this.isModal ? document.querySelector('app-mobile-modal-body') : window;
        fromEvent(stickyElm, 'scroll')
          .pipe(debounceTime(10), takeUntil(this.destroyed$))
          .subscribe({
            next: () => {
              this.stuckState = this.navPosition?.nativeElement?.getBoundingClientRect()?.top == this.topFromHeader;
              this.detectChanges();
            },
            error: (error) => {
              this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
              this.stuckState = false;
              this.detectChanges();
            },
          });
      }, 100);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.schoolService.setCurrentSchool(null);
    this.metaService.reset();
  }

  public trackReviewByFn(item: ReviewListItem): string {
    return item.entryId;
  }

  protected init() {
    this.init$$?.unsubscribe();
    this.init$$ = of(...[this.findTutors$, this.findClasses$, this.findLessons$, this.findReviews$])
      .pipe(
        mergeAll(),
        finalize(() => this.detectChanges()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get tabs() {
    const _tabs = schoolTabs;
    return _tabs.filter((t) => {
      if (this.meIsOwnerSchool) {
        return true;
      } else {
        switch (t.code) {
          case 'main':
            return true;
          case 'school-tutors':
            return this.hasTutors;
          case 'school-classes':
            return this.hasClasses;
          case 'feed':
          case 'educational-material':
            return this.hasLessons;
          case 'feedback':
            return this.hasFeedback;
        }
      }
    });
  }

  protected get findTutors$() {
    return this.accountService
      .findAccounts(
        {
          use: FindAccountStrategyEnum.school,
          school_id: [this._school.id],
          school_roles: [ESchoolParticipantRole.teacher],
        },
        this.defaultPagination,
      )
      .pipe(
        tap((results) => (this.hasTutors = !!results.results.length)),
        catchError((err) => {
          captureException(err);
          return of(null);
        }),
      );
  }

  protected get findClasses$() {
    return this.userServicesService
      .getServices(
        {
          use: FindUserServiceStrategyEnum.school,
          school_id: this._school.id,
        },
        this.defaultPagination,
      )
      .pipe(
        tap((results) => (this.hasClasses = !!results.results.length)),
        catchError((err) => {
          captureException(err);
          return of(null);
        }),
      );
  }

  protected get findLessons$() {
    return this.contentService
      .getContent(
        {
          use: FindContentStrategyEnum.school,
          school_id: this._school.id,
          content_type: LessonContentType,
        },
        {
          page: 1,
          pageSize: 100,
        },
        false,
      )
      .pipe(
        tap((results) => {
          this.lessonsItems = results.results;
          this.hasLessons = !!results.results.length;
        }),
        catchError((err) => {
          captureException(err);
          return of(null);
        }),
      );
  }

  protected get findReviews$() {
    return this.commentService.findReviews('school', this.school.id).pipe(
      tap((results) => {
        this.hasFeedback = !!results.results.length;
        this.reviews = results.results;
        this.afterLoaded();
      }),
      catchError((err) => {
        captureException(err);
        return of(null);
      }),
    );
  }

  public trackByFn(item: IContentFilterBlock): number {
    return item.order;
  }

  public get school(): CSchool {
    return this._school;
  }

  private get panesGenerated() {
    return this.tutors && this.feedback && this.lessons;
  }

  private generatePanes() {
    this.ownersIds = this._school.participants
      .filter((_part) => _part.role === ESchoolParticipantRole.owner)
      .map((_own) => _own.account_id);

    this.teachersIds = this._school.participants
      .filter((_part) => _part.role === ESchoolParticipantRole.teacher)
      .map((_own) => _own.account_id);

    let order = 0;

    this.tutors = {
      order: order++,
      format: EContentPaneFormat['new-grid-universal-card'],
      infinity: true,
      new_grid_view: 'vertical',
      loaded: false,
      userFilter: {
        use: FindAccountStrategyEnum.school,
        school_id: [this._school.id],
        school_roles: [ESchoolParticipantRole.teacher],
        subject_id: this.subjectId,
      },
    };

    this.feedback = {
      order: order++,
      format: EContentPaneFormat['meetings-reviews'],
      loaded: false,
      meetingReviewsFiler: {
        author_id: [...this.ownersIds, ...this.teachersIds],
      },
    };

    this.lessons = {
      order: order++,
      format: EContentPaneFormat['new-grid-universal-card'],
      loaded: false,
      infinity: true,
      new_grid_view: 'vertical-square',
      max_element_count: 4,
      max_element_width: 200,
      default_element_width: 200,
      routerUrlCreate: '',
      replace_for_mobile: true,
      replace_for_mobile_pane: 'card-universal',
      ignore_page_filters: true,
      contentFilter: {
        use: FindContentStrategyEnum.school,
        school_id: this.school.id,
        content_type: LessonContentType,
        subject_id: this.subjectId,
        limit: 12,
      },
    };
  }

  public setSubjectFromPage(id: number) {
    this.subjectId = id;
    this.generatePanes();
  }

  public get meIsOwnerSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this._me?.id);
  }

  protected setupMeta() {
    this.metaService.setTitle([this._school.title]);
    this.metaService.setDescription(striptags(this._school.description));
    this.metaService.setUrl(SchoolsService.schoolRoute(this._school, false).join('/'));
    this.metaService.setImage(this._school.cover_id);
  }

  public setActiveTab(item: IContentFilterBlock) {
    if (this.pathName === item.code) {
      return;
    }

    this.freezeTimer = true;
    this.screenService.setContainerClass('school-content', 'is-background', true);

    this.setAndResetUrl(SchoolsService.schoolRoute(this._school, false).join('/') + '/' + item.code);
    if (!this.panesGenerated) {
      this.generatePanes();
    }
    this.setTimeout(
      () => {
        this.pathName = item?.code;
        this.detectChanges();
      },
      item.code === 'main' ? 2000 : 1000,
    );
  }

  private setAndResetUrl(url: string) {
    this.location.replaceState(url);
  }

  public afterLoaded() {
    this.setTimeout(
      () => {
        this.screenService.setContainerClass('school-content', 'is-background', false);
        this.freezeTimer = false;
        this.detectChanges();
      },
      this.pathName === 'main' ? 500 : 10,
    );
  }

  public getVisibility(page: string) {
    return this.pathName === page ? 'visible' : 'hidden';
  }

  public showContentSelector() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {
        this.isContentSelectorOpened = true;
        this.isContentSelectorVisible = true;
        this.detectChanges();
      });
    } else {
      this.isContentSelectorOpened = true;
      this.isContentSelectorVisible = true;
    }
  }

  public hideContentSelector() {
    this.isContentSelectorVisible = false;
  }

  public checkIfHasLessons(e) {
    this.hasLessons = e;
    this.afterLoaded();
    this.detectChanges();
  }

  public addContent(items: Content[]) {
    const content_id = items[0].id;
    this.contentService
      .addToSchoolContent(this.school.id, content_id)
      .pipe(
        switchMap(() => this.findLessons$),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          this.generatePanes();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }
}
